<template>
  <div>
    <div class="wrapper">
      <ValidationProvider
        :ref="validationProviderRef"
        v-slot="{ validate}"
        :rules="rules"
        :name="label"
        :vid="validationProviderRef"
        :debounce="validationDelay"
      >
        <div
          :class="[allErrors.length? 'md-invalid' : '']"
        >
          <SlotScopeWatcher
            :id="label"
            v-model="mutableRawText"
            @change="validate(mutableRawText)"
          />
          <div style="position: relative;">
            <vue-editor
              :ref="textAreaRef"
              :key="keyRerender"
              :value="mutableFormattedText"
              :placeholder="placeholder"
              :disabled="disabled"
              :customModules="quillEditorSettings.customModulesForEditor"
              :editorOptions="quillEditorSettings.editorSettings"
              :editorToolbar="quillEditorSettings.customToolbar"
              :class="{newMode: isNew, newViewMode: isNew}"
              @input="onTextInput($event)|| validateText() "
            />
            <span
              v-if="enableCountCharModule"
              :id="counterContainerId"
              :class="[mutableRawText && mutableRawText.length > rules.max ? 'counterError': '', 'counter']"
            />
          </div>
          <slot name="editorBottom" />
          <div class="errorContainer">
            <span
              v-if="allErrors.length"
              class="md-error"
            >
              {{ allErrors[0] }}
            </span>
          </div>
        </div>
      </ValidationProvider>
    </div>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import { ValidationProvider } from 'vee-validate';
import Counter from '@/components/shared/widgets/forms/comment/QuillModules/CharCounterModule';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import validationProviderHelperMixin from '@/components/shared/widgets/forms/validationHelper/validationProviderHelperMixin';

export default {
    name: 'QuillEditorWithoutFiles',
    components: {
        VueEditor,
        ValidationProvider,
        SlotScopeWatcher,
    },
    mixins: [validationProviderHelperMixin],
    props: {
        label: {
            type: String,
            default: 'Comment',
        },
        placeholder: {
            type: String,
            default: null,
        },
        rules: {
            type: Object,
            default: () => ({
                required: false, min: 1, max: 1500,
            }),
        },
        quillEditorSettings: {
            type: Object,
            default: () => ({
                customToolbar: [],
                customModulesForEditor: [],
                editorSettings: {
                    modules: {},
                },
            }),
        },
        value: {
            type: String,
            default: null,
        },
        extraErrors: {
            type: Array,
            default: () => [],
        },
        sendFormattedText: {
            type: Boolean,
            default: false,
        },
        isNew: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mutableFormattedText: this.sendFormattedText ? this.value : null,
            mutableRawText: this.sendFormattedText ? null : this.value,
            counterContainerId: `counterId_${this.$utils.generateRandomString()}`,
            textAreaRef: this.$utils.generateRandomString(),
            textErrors: [],
            mutableExtraErrors: [],
            allErrors: [],
            keyRerender: 0,
        };
    },
    computed: {
        quill() {
            if (!this.vueEditorComponent()) return null;
            return this.vueEditorComponent().quill;
        },
        enableCountCharModule() {
            return this.rules.max;
        },
    },
    watch: {
        extraErrors: {
            handler() {
                this.setExtraErrors();
            },
            immediate: true,
            deep: true,
        },
        disabled: {
            handler(val) {
                if (!val) {
                    this.waitForTextEditor().then(() => {
                        this.quill.focus();
                    });
                }
            },
            immediate: true,
        },
        value: {
            handler() {
                if (this.sendFormattedText) this.mutableFormattedText = this.value;
                else {
                    this.mutableFormattedText = this.value;
                    this.mutableRawText = this.value;
                }
            },
            immediate: true,
        },
        placeholder: {
            handler() {
                this.forceRerender();
            },
            immediate: true,
            deep: true,

        },
    },
    created() {
        this.buildEditorConfig();
    },
    mounted() {
        this.waitForTextEditor().then(() => {
            this.mutableRawText = this.getInnerText();
        });
    },
    methods: {
        vueEditorComponent() {
            return this.$refs[this.textAreaRef];
        },
        async waitForTextEditor() {
            // We have to wait virtual dom update till textEditor is ready (as Vue does not allow to watch directly DOM element)
            while (!this.vueEditorComponent()) {
                // Note: disabled rules, I don't know what's for, this component should be refactor
                // eslint-disable-next-line no-await-in-loop
                await this.$nextTick();
            }
            return true;
        },
        getInnerText() {
            if (!this.vueEditorComponent()) return null;
            if (this.quill.container.innerText === '\n' && this.quill.getLength() === 1) return '';
            return this.quill.container.innerText;
        },
        buildEditorConfig() {
            if (this.enableCountCharModule) {
                this.addEditorModules(Counter, 'counter', {
                    counterContainer: `#${this.counterContainerId}`,
                    maxChar: this.rules.max,
                });
            }
        },
        addEditorModules(module, alias, options) {
            this.quillEditorSettings.customModulesForEditor.push({ alias, module });
            this.quillEditorSettings.editorSettings.modules[alias] = options;
        },
        onTextInput(formattedText) {
            this.mutableFormattedText = formattedText;
            this.mutableRawText = this.getInnerText();
            this.sendDataToParent();
        },
        async validateText() {
            await this.$nextTick();
            if (this.$refs[this.validationProviderRef]) this.setTextErrors((await this.validateValidationProvider(true)).errors);
        },
        setTextErrors(errors) {
            this.allErrors = this.allErrors.filter(error => !this.textErrors.find(textError => textError === error));
            this.textErrors = errors;
            this.allErrors = this.allErrors.concat(this.textErrors);
        },
        setExtraErrors() {
            this.allErrors = this.allErrors.filter(error => !this.mutableExtraErrors.find(extraError => extraError === error));
            this.mutableExtraErrors = this.extraErrors;
            this.allErrors = this.allErrors.concat(this.mutableExtraErrors);
        },
        sendDataToParent() {
            this.$emit('input', this.sendFormattedText ? this.mutableFormattedText : this.mutableRawText);
        },
        forceRerender() {
            this.keyRerender += 1;
        },
    },
};
</script>
<style lang="scss">
  .ql-editor{
    min-height: 100px !important;
    p:last-child {
      margin-bottom: 20px !important;
    }
  }
  .viewMode .ql-editor {
    min-height: 5px !important;
    p:last-child {
      margin-bottom: 0 !important;
    }
  }
  .viewMode .ql-container {
    border: unset !important;
  }
  .viewMode .newViewMode .ql-container {
    border: 1px solid !important;
    border-color: #777777 !important;
  }
  .viewMode .ql-toolbar{
    display: none;
  }
</style>
<style lang="scss" scoped>
  .viewMode .errorContainer {
    display: none;
  }
  .wrapper{
    position:relative;
    margin-top: 10px;
    margin-bottom: 20px;
  }
  .counterError{
    color: #ff1744 !important;
  }
  .counter, .md-error {
    left: unset;
  }
  .counter {
    color: rgba(0, 0, 0, 0.54);
    font-size: 12px;
    position: absolute;
    bottom: 10px;
    right: 4%;
    z-index: 10;
  }
  .viewMode .counter {
      display: none;
  }
  .md-field {
    all: unset;
  }
  .md-field:before {
    position: relative;
  }
  .md-field:after {
    position: relative;
  }
</style>
